import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumericField } from '../../components/NumericField';
import { getNumericFieldError } from '../../utils/numericValidation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { TabsContent } from '../../components/ui/tabs';
import { Plus, Trash2, Edit2, Loader2 } from 'lucide-react';

export const SFMetalTypesSection = ({
  // Data
  storefrontMetalPanelTypes,
  selectedSFMetalTypeIds,
  newSFMetalType,
  editingSFMetalType,
  showSFMetalTypeDialog,
  operationLoading,
  // Permissions
  canManageProject,
  // Handlers
  setNewSFMetalType,
  setShowSFMetalTypeDialog,
  selectAllItems,
  confirmBulkDelete,
  toggleItemSelection,
  handleCreateSFMetalType,
  handleEditSFMetalType,
  handleDeleteSFMetalType,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const thicknessRules = { required: true, min: 0, allowFraction: true, label: 'Thickness' };
  const handleSubmit = (e) => {
    setSubmitAttempted(true);
    if (getNumericFieldError(newSFMetalType.thickness_mm, thicknessRules)) {
      e.preventDefault();
      return;
    }
    handleCreateSFMetalType(e);
  };

  return (
    <TabsContent value="sf-metal-types">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Metal Panel Types</h2>
        <div className="flex gap-2">
          {canManageProject && storefrontMetalPanelTypes.length > 0 && (
            <>
              <Button variant="outline" onClick={() => selectAllItems('sfMetalType')}>
                {selectedSFMetalTypeIds.length === storefrontMetalPanelTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={() => confirmBulkDelete('sfMetalType')}
                disabled={selectedSFMetalTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedSFMetalTypeIds.length})
              </Button>
            </>
          )}
          {canManageProject && (
            <Dialog open={showSFMetalTypeDialog} onOpenChange={(open) => { setSubmitAttempted(false); setShowSFMetalTypeDialog(open); }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Metal Type
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingSFMetalType ? 'Edit Metal Panel Type' : 'Create Metal Panel Type'}</DialogTitle>
                <DialogDescription>Define metal panel specifications for storefront</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type (e.g., MT1, MT2) *</Label>
                    <Input value={newSFMetalType.name} onChange={(e) => setNewSFMetalType({...newSFMetalType, name: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Manufacturer *</Label>
                    <Input value={newSFMetalType.manufacturer} onChange={(e) => setNewSFMetalType({...newSFMetalType, manufacturer: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Model *</Label>
                    <Input value={newSFMetalType.model} onChange={(e) => setNewSFMetalType({...newSFMetalType, model: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Thickness (in) *</Label>
                    <NumericField value={newSFMetalType.thickness_mm} onChange={(v) => setNewSFMetalType({...newSFMetalType, thickness_mm: v})} rules={thicknessRules} showError={submitAttempted} placeholder="e.g., 0.125" required data-testid="sf-metal-thickness-input" errorTestId="sf-metal-thickness-error" />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Color Code (HEX/RAL)</Label>
                    <Input value={newSFMetalType.colorCode} onChange={(e) => setNewSFMetalType({...newSFMetalType, colorCode: e.target.value})} placeholder="#FFFFFF or RAL 9016" />
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={operationLoading}>
                  {operationLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingSFMetalType ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    editingSFMetalType ? 'Update Metal Type' : 'Create Metal Type'
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {storefrontMetalPanelTypes.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg">
          <p>No metal panel types created yet</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canManageProject && (
                  <th className="w-12 px-3 py-3 text-left">
                    <Checkbox
                      checked={selectedSFMetalTypeIds.length === storefrontMetalPanelTypes.length && storefrontMetalPanelTypes.length > 0}
                      onCheckedChange={() => selectAllItems('sfMetalType')}
                    />
                  </th>
                )}
                <th className="w-20 px-3 py-3 text-left font-semibold text-slate-700">Type</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Model</th>
                <th className="w-24 px-3 py-3 text-left font-semibold text-slate-700">Thickness</th>
                <th className="w-28 px-3 py-3 text-left font-semibold text-slate-700">Color Code</th>
                {canManageProject && (
                  <th className="w-20 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {storefrontMetalPanelTypes.map((metal, idx) => (
                <tr 
                  key={metal.id}
                  className={`border-b hover:bg-slate-50 transition-colors ${
                    selectedSFMetalTypeIds.includes(metal.id) ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                  }`}
                >
                  {canManageProject && (
                    <td className="w-12 px-3 py-2">
                      <Checkbox
                        checked={selectedSFMetalTypeIds.includes(metal.id)}
                        onCheckedChange={(checked) => toggleItemSelection(metal.id, 'sfMetalType', { target: { checked } })}
                      />
                    </td>
                  )}
                  <td className="w-20 px-3 py-2 font-medium text-slate-900">{metal.name}</td>
                  <td className="px-3 py-2 text-slate-600 truncate">{metal.manufacturer || '-'}</td>
                  <td className="px-3 py-2 text-slate-600 truncate">{metal.model || '-'}</td>
                  <td className="w-24 px-3 py-2 text-slate-600">{metal.thickness_mm ? `${metal.thickness_mm}"` : '-'}</td>
                  <td className="w-28 px-3 py-2 text-slate-600">{metal.colorCode || '-'}</td>
                  {canManageProject && (
                    <td className="w-20 px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditSFMetalType(metal)}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white" onClick={() => handleDeleteSFMetalType(metal.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TabsContent>
  );
};

export default SFMetalTypesSection;
